import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE, fmtUSD } from "@/lib/api";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { toast } from "sonner";
import {
  Film, TrendingUp, TrendingDown, Loader2, Sparkles, Download, Share2, Rocket, Lock, Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import VideoPlayer from "@/components/VideoPlayer";
import ReplayTabs from "@/components/ReplayTabs";

// Weekly Best/Worst Reel
// - Backend picks top 3 win + top 3 loss clips of the last 7 days
// - Client concatenates them into ONE montage MP4 using FFmpeg.wasm concat demuxer
// - Uploads via POST /api/video/reel/save (auto-starred, shareable with watermark)

const ffmpeg = new FFmpeg();
let ffmpegLoadedPromise = null;
const loadFfmpeg = async () => {
  if (ffmpegLoadedPromise) return ffmpegLoadedPromise;
  ffmpegLoadedPromise = (async () => {
    const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
    await ffmpeg.load({
      coreURL: `${baseURL}/ffmpeg-core.js`,
      wasmURL: `${baseURL}/ffmpeg-core.wasm`,
    });
    return ffmpeg;
  })();
  return ffmpegLoadedPromise;
};

export default function WeeklyReel() {
  const navigate = useNavigate();
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });
  const isPro = billing?.plan_tier === "pro";

  const { data: picks, isLoading, refetch } = useQuery({
    queryKey: ["weekly-reel-picks"],
    queryFn: async () => (await api.get("/video/reel/weekly")).data,
    enabled: isPro,
  });

  const [building, setBuilding] = useState(false);
  const [progress, setProgress] = useState("");
  const [reelUrl, setReelUrl] = useState(null);   // object URL of the finished montage
  const [savedClip, setSavedClip] = useState(null);

  const allClips = useMemo(() => {
    if (!picks) return [];
    return [...(picks.best || []), ...(picks.worst || [])];
  }, [picks]);

  const totalPnl = useMemo(() =>
    (allClips || []).reduce((s, c) => s + (Number(c.pnl) || 0), 0),
    [allClips]
  );

  const buildReel = async () => {
    if (allClips.length === 0) return;
    setBuilding(true);
    setReelUrl(null);
    setSavedClip(null);
    try {
      setProgress("Loading FFmpeg…");
      const ff = await loadFfmpeg();
      const token = encodeURIComponent(localStorage.getItem("edge_token") || "");

      // Download each clip into ffmpeg's virtual FS
      const inputNames = [];
      const segmentMarkers = [];
      let runningOffset = 0;
      for (let i = 0; i < allClips.length; i++) {
        const clip = allClips[i];
        setProgress(`Fetching clip ${i + 1}/${allClips.length} · ${clip.symbol}`);
        const url = `${API_BASE}/video/clips/${clip.id}/stream?token=${token}`;
        const data = await fetchFile(url);
        const name = `in${i}.mp4`;
        await ff.writeFile(name, data);
        inputNames.push(name);
        segmentMarkers.push({
          time: runningOffset,
          symbol: clip.symbol,
          pnl: clip.pnl,
          kind: (clip.pnl || 0) >= 0 ? "win" : "loss",
        });
        runningOffset += clip.duration_seconds || 0;
      }

      // Write concat list file
      const list = inputNames.map((n) => `file '${n}'`).join("\n");
      await ff.writeFile("list.txt", new TextEncoder().encode(list));

      setProgress("Stitching montage…");
      // Concat with re-encode to a controlled bitrate so the resulting reel
      // always fits under the ingress upload limit (was hitting 413s with
      // stream-copy on 6-clip reels — different source bitrates = unpredictable
      // output size). 2 Mbps @ 720p is broadcast-quality for a screen-recording
      // trading session, and keeps a 6-min reel around 90 MB tops.
      await ff.exec([
        "-f", "concat",
        "-safe", "0",
        "-i", "list.txt",
        "-c:v", "libx264",
        "-preset", "veryfast",
        "-b:v", "2M",
        "-maxrate", "2500k",
        "-bufsize", "5M",
        "-vf", "scale='min(1280,iw)':-2",  // cap width at 1280, preserve aspect
        "-c:a", "aac",
        "-b:a", "96k",
        "-movflags", "+faststart",  // moov atom at start → instant playback + range-seek
        "reel.mp4",
      ]);

      setProgress("Reading finished reel…");
      const reelData = await ff.readFile("reel.mp4");
      const reelBlob = new Blob([reelData.buffer], { type: "video/mp4" });
      const url = URL.createObjectURL(reelBlob);
      setReelUrl(url);

      // Upload to backend for storage + sharing
      setProgress("Saving reel to your library…");
      const fd = new FormData();
      fd.append("file", reelBlob, "weekly_reel.mp4");
      fd.append("meta", JSON.stringify({
        title: `Weekly Reel · ${picks.week_start} → ${picks.week_end}`,
        total_pnl: totalPnl,
        duration_seconds: runningOffset,
        segment_markers: segmentMarkers,
      }));
      const r = await api.post("/video/reel/save", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000, // 10 min — reels can be 50-200 MB
      });
      setSavedClip(r.data);

      // Clean up ffmpeg FS
      for (const n of inputNames) { try { await ff.deleteFile(n); } catch { /* */ } }
      try { await ff.deleteFile("list.txt"); } catch { /* */ }
      try { await ff.deleteFile("reel.mp4"); } catch { /* */ }

      setProgress("Done!");
      toast.success("Weekly reel ready — shareable link below.");
    } catch (e) {
      console.error(e);
      toast.error(`Reel build failed: ${e.message || e}`);
    } finally {
      setBuilding(false);
    }
  };

  const shareReel = async () => {
    if (!savedClip) return;
    try {
      let token = savedClip.share_token;
      if (!token) {
        const r = await api.post(`/video/clips/${savedClip.id}/share`);
        token = r.data.share_token;
      }
      const url = `${window.location.origin}/clip/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch (e) { toast.error("Share failed"); }
  };

  if (!billing) return <div className="p-8 text-sm" style={{ color: "var(--fg-3)" }}>Loading…</div>;

  if (!isPro) {
    return (
      <div>
        <ReplayTabs />
        <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Film className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>WEEKLY REEL · PRO</div>
        </div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight mb-4">
          Your best trades. Your worst trades. <span style={{ color: "var(--accent)" }}>One 90-second reel.</span>
        </h1>
        <p className="text-base leading-relaxed mb-8" style={{ color: "var(--fg-2)" }}>
          Every Sunday, TradeGreen auto-picks your top 3 wins and top 3 losses from the week and stitches them into a
          shareable montage. Post it to X, drop it in your Discord, or study the pattern yourself. Available on Pro.
        </p>
        <div
          className="p-8"
          style={{
            background: "linear-gradient(180deg, rgba(168,85,247,0.06), var(--surface) 60%)",
            border: "1px solid rgba(168,85,247,0.35)",
            borderRadius: "16px",
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Lock className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <div className="label-eyebrow" style={{ color: "var(--accent)" }}>PRO-TIER FEATURE</div>
          </div>
          <button
            onClick={() => navigate("/pricing")}
            className="px-5 py-3 text-sm font-semibold tracking-wide flex items-center gap-2"
            style={{ background: "var(--accent)", color: "white", borderRadius: "10px", boxShadow: "0 4px 24px rgba(168,85,247,0.4)" }}
          >
            <Rocket className="h-4 w-4" strokeWidth={2} /> UPGRADE TO PRO
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ReplayTabs />
      <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <Film className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <div className="label-eyebrow" style={{ color: "var(--accent)" }}>WEEKLY REEL</div>
      </div>
      <h1 className="font-heading text-4xl font-semibold tracking-tight mb-2">Your last 7 days · in one reel</h1>
      <p className="text-sm mb-8" style={{ color: "var(--fg-2)" }}>
        {picks?.week_start && (<>Picks from <span className="font-mono-num">{picks.week_start}</span> → <span className="font-mono-num">{picks.week_end}</span>. Top wins first, worst losses after.</>)}
      </p>

      {isLoading && <div className="text-sm" style={{ color: "var(--fg-3)" }}>Loading picks…</div>}

      {picks && allClips.length === 0 && (
        <div className="p-8 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "14px", color: "var(--fg-2)" }} data-testid="reel-empty">
          <Sparkles className="h-6 w-6 mx-auto mb-2" strokeWidth={1.5} style={{ color: "var(--fg-3)" }} />
          No clips this week yet. Upload an OBS recording from <a href="/replay" style={{ color: "var(--accent)" }}>Replay</a> and come back Sunday.
        </div>
      )}

      {picks && allClips.length > 0 && (
        <>
          {/* Best row */}
          {picks.best?.length > 0 && (
            <div className="mb-6" data-testid="reel-best">
              <div className="label-eyebrow mb-3 flex items-center gap-1.5" style={{ color: "var(--green)" }}>
                <TrendingUp className="h-3 w-3" strokeWidth={2} /> BEST TRADES OF THE WEEK
              </div>
              <ClipRow clips={picks.best} />
            </div>
          )}

          {/* Worst row */}
          {picks.worst?.length > 0 && (
            <div className="mb-8" data-testid="reel-worst">
              <div className="label-eyebrow mb-3 flex items-center gap-1.5" style={{ color: "var(--loss)" }}>
                <TrendingDown className="h-3 w-3" strokeWidth={2} /> WORST TRADES OF THE WEEK
              </div>
              <ClipRow clips={picks.worst} />
            </div>
          )}

          <DiscordSection />

          <div className="p-5 mb-6 flex items-center gap-4 flex-wrap" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
            <div>
              <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>REEL SUMMARY</div>
              <div className="mt-1 font-mono-num text-sm" style={{ color: "var(--fg)" }}>
                {allClips.length} clips ·{" "}
                <span style={{ color: totalPnl >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(totalPnl)}</span> net P&amp;L
              </div>
            </div>
            <div className="flex-1" />
            <button
              onClick={buildReel}
              disabled={building}
              data-testid="build-reel-btn"
              className="px-5 py-3 text-sm font-semibold tracking-wide flex items-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "var(--accent)", color: "white", borderRadius: "10px", boxShadow: building ? "none" : "0 4px 24px rgba(168,85,247,0.4)" }}
            >
              {building ? (<><Loader2 className="h-4 w-4 animate-spin" /> {progress}</>) : (<><Sparkles className="h-4 w-4" /> STITCH REEL NOW</>)}
            </button>
          </div>

          {/* Finished reel */}
          {reelUrl && savedClip && (
            <div className="mb-6" data-testid="finished-reel">
              <div className="label-eyebrow mb-3" style={{ color: "var(--accent)" }}>YOUR REEL · READY TO SHARE</div>
              <VideoPlayer
                clip={savedClip}
                streamUrl={reelUrl}
              />
              <div className="mt-3 flex items-center gap-3">
                <a
                  href={reelUrl}
                  download="tradegreen_weekly_reel.mp4"
                  className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5"
                  style={{ background: "var(--surface)", color: "var(--fg-2)", border: "1px solid var(--border)", borderRadius: "8px" }}
                  data-testid="reel-download-btn"
                >
                  <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> DOWNLOAD
                </a>
                <button
                  onClick={shareReel}
                  className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5"
                  style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
                  data-testid="reel-share-btn"
                >
                  <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} /> COPY SHARE LINK
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

const ClipRow = ({ clips }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
    {clips.map((c) => (
      <div key={c.id} className="p-3" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "10px" }}>
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold" style={{ color: "var(--fg)" }}>{c.symbol}</span>
          <span className="font-mono-num" style={{ color: (c.pnl || 0) >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(c.pnl)}</span>
        </div>
        <div className="text-[11px] mt-1 font-mono-num" style={{ color: "var(--fg-3)" }}>
          {c.date} · {c.side?.toUpperCase()} · {Math.round(c.duration_seconds || 0)}s
        </div>
      </div>
    ))}
  </div>
);

// Per-user Discord webhook — auto-notifies when a reel is saved.
const DiscordSection = () => {
  const { data } = useQuery({
    queryKey: ["discord-webhook"],
    queryFn: async () => (await api.get("/user/discord")).data,
  });
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (data?.webhook_url !== undefined) setUrl(data.webhook_url);
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/user/discord", { webhook_url: url });
      toast.success(url ? "Discord webhook saved" : "Discord notifications disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/user/discord/test");
      toast.success("Test ping sent — check your Discord");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const configured = !!data?.webhook_url;

  return (
    <div
      className="mb-6 overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}
      data-testid="discord-section"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div style={{ background: "rgba(88,101,242,0.15)", padding: 8, borderRadius: 8 }}>
          <Zap className="h-4 w-4" strokeWidth={2} style={{ color: "#7289da" }} />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow" style={{ color: configured ? "var(--green)" : "var(--fg-3)" }}>
            DISCORD · {configured ? "ACTIVE · YOU'LL BE NOTIFIED WHEN A REEL IS READY" : "DISABLED"}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--fg-2)" }}>
            {configured
              ? "Weekly reels + share links auto-post to your Discord channel."
              : "Add a Discord webhook to auto-ping yourself when your reel is ready."}
          </div>
        </div>
        <div className="text-xs" style={{ color: "var(--fg-3)" }}>{expanded ? "HIDE" : "SETUP"}</div>
      </button>

      {expanded && (
        <div className="p-4 pt-0 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-xs mt-3 mb-3 leading-relaxed" style={{ color: "var(--fg-3)" }}>
            <strong style={{ color: "var(--fg-2)" }}>How to get a webhook URL:</strong> In Discord →
            Server settings → Integrations → Webhooks → New Webhook → pick a channel → <em>Copy Webhook URL</em>. Paste it below.
          </div>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
            data-testid="discord-webhook-input"
            className="w-full px-3 py-2.5 text-xs font-mono-num focus:outline-none"
            style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="discord-save-btn"
              className="flex-1 py-2 text-xs font-semibold disabled:opacity-50"
              style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
            >
              {saving ? "…" : url ? "SAVE" : "DISABLE"}
            </button>
            {configured && (
              <button
                onClick={test}
                disabled={testing}
                data-testid="discord-test-btn"
                className="px-3 py-2 text-xs font-semibold disabled:opacity-50"
                style={{ background: "var(--surface2)", color: "var(--fg-2)", border: "1px solid var(--border)", borderRadius: "8px" }}
              >
                {testing ? "…" : "SEND TEST PING"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
